/**
 * Slide-in drill-down panel for a single student.
 * Driven by ?student=<id> on the Students tab URL — opening sets it,
 * closing clears it, browser back closes the panel without navigating.
 *
 * Per spec §6:
 *   - 720px fixed width, slides in from the right
 *   - Dark backdrop captures click to dismiss
 *   - Header KPI row (3 cards)
 *   - 9-week dual-axis chart
 *   - Chapter list with per-gap details
 *
 * Single subject — no subject tabs (plan §4.2 simplification).
 */

import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import api from '../../services/api';
import WeeklyProgressChart from './WeeklyProgressChart';

const SUBJECT_LABEL = { physics: 'Physics', chemistry: 'Chemistry', mathematics: 'Maths' };

function trendArrow(direction) {
  if (direction === 'up') return <span className="text-teacher-green">↑</span>;
  if (direction === 'down') return <span className="text-teacher-red">↓</span>;
  return <span className="text-teacher-text-tertiary">→</span>;
}

function trendDeltaText(deltaPct) {
  const sign = deltaPct > 0 ? '+' : '';
  return deltaPct === 0 ? '0%' : `${sign}${deltaPct}%`;
}

function KpiCard({ label, value, trend, note }) {
  return (
    <div className="bg-teacher-bg rounded-lg px-3.5 py-3">
      <div className="text-[10px] uppercase tracking-wider text-teacher-text-secondary font-medium mb-1.5">
        {label}
      </div>
      <div className="text-[22px] font-medium leading-none">
        {value}
        {trend && (
          <span className="text-xs ml-1 font-normal">
            {trendArrow(trend.direction)}
            <span className="text-teacher-text-secondary ml-0.5">
              {trendDeltaText(trend.delta_pct)}
            </span>
          </span>
        )}
      </div>
      {note && (
        <div className="text-[11px] text-teacher-text-secondary mt-1">{note}</div>
      )}
    </div>
  );
}

function GapItem({ gap }) {
  const status = gap.status || 'active';
  const dotColor =
    status === 'cleared' ? 'bg-teacher-green' :
    status === 'capsule_in_progress' ? 'bg-teacher-amber' :
    'bg-teacher-red';
  const badge =
    status === 'cleared' ? { label: 'Cleared', cls: 'bg-teacher-green-light text-teacher-green' } :
    status === 'capsule_in_progress' ? { label: 'Capsule in progress', cls: 'bg-teacher-amber-light text-teacher-amber' } :
    { label: 'Open gap', cls: 'bg-teacher-red-light text-teacher-red' };

  return (
    <div className="flex items-start gap-2 p-2.5 bg-teacher-bg rounded-md">
      <div className={`w-2 h-2 rounded-full shrink-0 mt-1.5 ${dotColor}`} />
      <div className="flex-1 min-w-0">
        <div className="text-[12px] font-medium text-teacher-text-primary mb-0.5">
          {gap.node_name}
        </div>
        {gap.core_misconception && (
          <div className="text-[11px] text-teacher-text-secondary leading-snug mb-1.5">
            {gap.core_misconception}
          </div>
        )}
        {Array.isArray(gap.structural_rule) && gap.structural_rule.length > 0 && (
          <ol className="text-[10px] text-teacher-text-secondary italic border-l-2 border-teacher-border-strong pl-2 mb-1.5 leading-relaxed list-decimal list-inside">
            {gap.structural_rule.map((step, i) => <li key={i}>{step}</li>)}
          </ol>
        )}
        {Array.isArray(gap.micro_skills) && gap.micro_skills.length >= 2 && (
          <div className="mt-2">
            <div className="text-[10px] uppercase tracking-wider text-teacher-text-tertiary font-medium mb-1.5">
              Micro-skills in this gap
            </div>
            {gap.micro_skills.map((ms, i) => {
              const ratio = ms.attempts > 0 ? ms.correct / ms.attempts : 0;
              const correctCls =
                ratio >= 0.6 ? 'text-teacher-green' :
                ratio >= 0.4 ? 'text-teacher-amber' :
                'text-teacher-red';
              const incorrect = (ms.attempts || 0) - (ms.correct || 0);
              return (
                <div key={i} className="flex items-center gap-2 py-1 border-t border-teacher-border text-[11px]">
                  <div className="flex-1 truncate">{ms.name}</div>
                  <div className="text-[9px] text-teacher-text-tertiary bg-teacher-bg-secondary border border-teacher-border px-1.5 py-px rounded font-teacher-mono">
                    {ms.category}
                  </div>
                  <div className="text-teacher-text-secondary text-right shrink-0 min-w-[100px]">
                    {ms.attempts} attempts ·{' '}
                    <span className={`font-medium ${correctCls}`}>{ms.correct} correct</span>
                    {' · '}{incorrect} incorrect
                  </div>
                </div>
              );
            })}
          </div>
        )}
        {(gap.first_seen || gap.attempts_total) && (
          <div className="flex gap-2 flex-wrap mt-2">
            {gap.first_seen && (
              <span className="text-[10px] bg-teacher-bg-secondary border border-teacher-border text-teacher-text-secondary px-2 py-0.5 rounded-full font-teacher-mono">
                First seen: {gap.first_seen}
              </span>
            )}
            {gap.attempts_total !== null && gap.attempts_total !== undefined && (
              <span className="text-[10px] bg-teacher-bg-secondary border border-teacher-border text-teacher-text-secondary px-2 py-0.5 rounded-full font-teacher-mono">
                Seen across: {gap.attempts_total} attempts
              </span>
            )}
          </div>
        )}
      </div>
      <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium whitespace-nowrap ${badge.cls}`}>
        {badge.label}
      </span>
    </div>
  );
}

function ChapterRow({ chapter }) {
  const acc = chapter.accuracy_pct || 0;
  const barColor = acc >= 70 ? 'bg-teacher-green' : acc >= 50 ? 'bg-teacher-amber' : 'bg-teacher-red';
  const txtColor = acc >= 70 ? 'text-teacher-green' : acc >= 50 ? 'text-teacher-amber' : 'text-teacher-red';

  return (
    <div className="border border-teacher-border rounded-lg overflow-hidden">
      <div className="flex items-center gap-2.5 px-3 py-2 bg-white">
        <div className="text-[12px] font-medium flex-1 truncate">{chapter.chapter_name}</div>
        {chapter.question_count > 0 && (
          <div className="text-[10px] text-teacher-text-tertiary whitespace-nowrap">
            {chapter.question_count} questions
          </div>
        )}
        <div className="w-[70px]">
          <div className="h-1 rounded-full bg-teacher-border overflow-hidden">
            <div className={`h-full rounded-full ${barColor}`} style={{ width: `${Math.min(100, acc)}%` }} />
          </div>
        </div>
        <div className={`text-[12px] font-medium min-w-[36px] text-right ${txtColor}`}>
          {acc}%
        </div>
      </div>
      {chapter.gaps && chapter.gaps.length > 0 && (
        <div className="px-3 pb-3 pt-0 flex flex-col gap-1.5">
          {chapter.gaps.map(g => <GapItem key={g.atlas_node_id} gap={g} />)}
        </div>
      )}
    </div>
  );
}

function panelInner({ data, subject, onClose }) {
  const kpis = data?.kpis || {};
  const ctxLabel = SUBJECT_LABEL[subject] || subject;

  return (
    <>
      <div className="px-6 py-4 border-b border-teacher-border flex items-center gap-3.5 sticky top-0 bg-white z-10">
        <button
          onClick={onClose}
          aria-label="Close drill-down"
          className="w-7 h-7 rounded-full border border-teacher-border bg-transparent text-teacher-text-secondary hover:bg-teacher-bg flex items-center justify-center"
        >
          ✕
        </button>
        <div className="flex-1 min-w-0">
          <div className="text-base font-medium text-teacher-text-primary truncate">
            {data?.student?.name || 'Student'}
          </div>
          <div className="text-xs text-teacher-text-secondary mt-0.5">
            {data?.student?.class ? `Class ${data.student.class}` : ''}
            {data?.student?.last_active ? ` · Last active: ${data.student.last_active}` : ''}
            {' · '}{ctxLabel}
          </div>
        </div>
      </div>

      <div className="p-6 space-y-5">
        <div className="grid grid-cols-3 gap-2.5">
          <KpiCard
            label="Accuracy · 60 days"
            value={`${kpis.accuracy_60d || 0}%`}
            trend={kpis.accuracy_trend}
            note="vs. last week"
          />
          <KpiCard
            label="Consistency · this week"
            value={`${kpis.consistency_this_week || 0} days`}
            note=""
          />
          <KpiCard
            label="Questions · this week"
            value={kpis.questions_this_week || 0}
            note={`${kpis.open_gap_count || 0} open gap${(kpis.open_gap_count || 0) === 1 ? '' : 's'} · ${kpis.cleared_gap_count || 0} cleared`}
          />
        </div>

        {data?.weekly_history?.length > 0 ? (
          <WeeklyProgressChart data={data.weekly_history} />
        ) : (
          <div className="bg-white border border-teacher-border rounded-xl px-4 py-8 text-center">
            <div className="text-sm text-teacher-text-secondary">
              Not enough practice history yet — chart appears after 2 weeks.
            </div>
          </div>
        )}

        <div>
          <div className="text-[12px] font-semibold uppercase tracking-wider text-teacher-text-primary mb-3">
            Chapter mastery & thinking gaps
          </div>
          {(data?.chapters?.length || 0) === 0 ? (
            <div className="text-xs text-teacher-text-secondary">
              No open thinking gaps in {ctxLabel}.
            </div>
          ) : (
            <div className="flex flex-col gap-2">
              {data.chapters.map(ch => (
                <ChapterRow key={ch.chapter_key} chapter={ch} />
              ))}
            </div>
          )}
        </div>
      </div>
    </>
  );
}

export default function StudentDrillPanel({ batchId, subject }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const studentId = searchParams.get('student');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Fetch when studentId changes
  useEffect(() => {
    if (!studentId) {
      setData(null);
      setError(null);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    api.getTeacherStudentDrillDownV2(batchId, subject, studentId)
      .then(r => { if (!cancelled) setData(r.data); })
      .catch(e => { if (!cancelled) setError(e.message); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [studentId, batchId, subject]);

  // Esc closes the panel
  useEffect(() => {
    if (!studentId) return;
    const onKey = (e) => { if (e.key === 'Escape') close(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [studentId]);

  function close() {
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      next.delete('student');
      return next;
    });
  }

  if (!studentId) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        onClick={close}
        className="fixed inset-0 bg-black/40 z-40"
        aria-hidden="true"
      />
      {/* Panel */}
      <aside
        className="fixed top-0 right-0 h-screen w-full max-w-[720px] bg-white border-l border-teacher-border z-50 overflow-y-auto shadow-[-8px_0_32px_rgba(0,0,0,0.1)] font-teacher text-teacher-text-primary"
        role="dialog"
        aria-label="Student drill-down"
      >
        {loading && (
          <div className="p-8 text-sm text-teacher-text-secondary">Loading…</div>
        )}
        {error && (
          <div className="p-6">
            <div className="bg-teacher-red-light border border-teacher-red text-teacher-red rounded-lg p-3 text-sm mb-3">
              Error: {error}
            </div>
            <button
              onClick={close}
              className="text-sm text-teacher-text-secondary hover:text-teacher-text-primary"
            >
              ← Close
            </button>
          </div>
        )}
        {!loading && !error && data && panelInner({ data, subject, onClose: close })}
      </aside>
    </>
  );
}
